<script setup>
// SiteUP CRM: formulario unico de nova tarefa (vistoria kanban-menus-agendamento-r1,
// opcao "1 - Formulario unico"; decisoes 13 e 20 do kanban-layout-r3).
//
// Todos os pontos de entrada abrem esta tela: botao Nova tarefa, "Abrir
// completo" da linha rapida, /tasks?new=1 vindo do Kanban, painel do contato e
// "mais opcoes" do chip da conversa. O editor de seis abas continua existindo
// para EDITAR tarefa ja criada (Tempo, Dependencias e Atividade).
//
// Subtarefas entram ja na criacao: a tarefa e criada primeiro e as subtarefas
// logo depois, pelo mesmo caminho unico de taskCreation.js. Subtarefa que falhar
// nao desfaz a tarefa: o rodape lista as que faltaram e repete so elas.
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import TasksAPI from 'dashboard/api/tasks';
import TaskLabelsAPI from 'dashboard/api/taskLabels';
import TaskTemplatesAPI from 'dashboard/api/taskTemplates';
import ContactAPI from 'dashboard/api/contacts';
import DatePicker from 'dashboard/components-next/datetime/DatePicker.vue';
import TaskEditorFrame from './TaskEditorFrame.vue';
import TaskAnchoredPopover from './TaskAnchoredPopover.vue';
import {
  buildDueAt,
  createTaskWithExtras,
  dueChipDate,
  initialStatusKey,
  toIsoDate,
} from './taskCreation';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const props = defineProps({
  // Vinculo que veio da origem (cartao, conversa ou contato). Chega travado e
  // o usuario pode trocar.
  link: { type: Object, default: null },
  initialTitle: { type: String, default: '' },
  initialDueDate: { type: String, default: null },
  initialAssigneeId: { type: Number, default: null },
  statuses: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'created']);

const { t, locale } = useI18n();
const store = useStore();

const MAX_ATTACHMENT_BYTES = 40 * 1024 * 1024;

const agents = computed(() => store.getters['agents/getAgents'] || []);
const currentUser = computed(() => store.getters.getCurrentUser || {});

const catalog = ref([...(props.statuses || [])]);
const statusesLoading = ref(false);
const statusesError = ref(false);

const title = ref(props.initialTitle || '');
const dueDate = ref(props.initialDueDate || null);
const dueTime = ref('');
const assignedToUserId = ref(
  props.initialAssigneeId || currentUser.value.id || null
);
const link = ref(props.link ? { ...props.link } : null);
const subtasks = ref([]);
const showMore = ref(false);
const description = ref('');
const priority = ref('medium');
const labelIds = ref([]);
const reminderOffsets = ref([]);
const recurrenceEnabled = ref(false);
const recurrenceFrequency = ref('daily');
const attachmentFile = ref(null);
const keepEditing = ref(false);

const availableLabels = ref([]);
const availableTemplates = ref([]);
const selectedTemplateId = ref(null);

const saving = ref(false);
const createError = ref('');
const titleError = ref(false);
const dueError = ref(false);
const createdTask = ref(null);
const failedSubtaskTitles = ref([]);
const attachmentError = ref('');
const attachmentUploading = ref(false);

const titleInput = ref(null);
const confirmingDiscard = ref(false);

const contactQuery = ref('');
const contactResults = ref([]);
const contactSearching = ref(false);
const contactSearchError = ref(false);
let contactSearchToken = 0;
let contactSearchTimer = null;
let alive = true;
onBeforeUnmount(() => {
  alive = false;
  if (contactSearchTimer) clearTimeout(contactSearchTimer);
});

const focusTitle = () => {
  nextTick(() => titleInput.value?.focus());
};
focusTitle();

const loadStatuses = async () => {
  if (catalog.value.length) return;
  const fromStore = store.getters['tasks/getStatuses'] || [];
  if (fromStore.length) {
    catalog.value = fromStore;
    return;
  }
  statusesLoading.value = true;
  statusesError.value = false;
  try {
    const { data } = await TasksAPI.list({ page: 1, per_page: 1 });
    if (!alive) return;
    catalog.value = Array.isArray(data?.statuses) ? data.statuses : [];
  } catch {
    // Sem catalogo a regra cai no padrao do produto, que ja e o primeiro nao
    // terminal. O aviso existe para o usuario saber que o status veio do padrao.
    if (alive) statusesError.value = true;
  } finally {
    if (alive) statusesLoading.value = false;
  }
};
loadStatuses();

const loadLabels = async () => {
  try {
    const { data } = await TaskLabelsAPI.list();
    if (alive) availableLabels.value = Array.isArray(data) ? data : [];
  } catch {
    if (alive) availableLabels.value = [];
  }
};
const loadTemplates = async () => {
  try {
    const { data } = await TaskTemplatesAPI.list();
    if (alive) availableTemplates.value = Array.isArray(data) ? data : [];
  } catch {
    if (alive) availableTemplates.value = [];
  }
};
loadLabels();
loadTemplates();

const statusKey = computed(() => initialStatusKey(catalog.value));

const chipKeys = ['today', 'tomorrow', 'workday'];
const chipDates = computed(() =>
  Object.fromEntries(chipKeys.map(key => [key, dueChipDate(key)]))
);
const workdayLabel = computed(() => {
  const date = chipDates.value.workday;
  if (!date) return '';
  if (date.getDay() === 1) return t('WORKSPACE_V2.TASK_CREATE.MONDAY');
  const name = date.toLocaleDateString(toIntlLocale(locale.value), {
    weekday: 'long',
  });
  return name.charAt(0).toUpperCase() + name.slice(1);
});
const chipLabel = key => {
  if (key === 'today') return t('WORKSPACE_V2.TASK_CREATE.TODAY');
  if (key === 'tomorrow') return t('WORKSPACE_V2.TASK_CREATE.TOMORROW');
  return workdayLabel.value;
};
const activeChip = computed(() =>
  chipKeys.find(key => toIsoDate(chipDates.value[key]) === dueDate.value)
);
const pickChip = key => {
  dueError.value = false;
  const iso = toIsoDate(chipDates.value[key]);
  dueDate.value = dueDate.value === iso ? null : iso;
};
const dueLabel = computed(() => {
  if (!dueDate.value) return t('WORKSPACE_V2.TASK_CREATE.NO_DUE');
  const [year, month, day] = dueDate.value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(
    toIntlLocale(locale.value),
    {
      day: '2-digit',
      month: 'short',
    }
  );
});

const assigneeName = computed(() => {
  if (!assignedToUserId.value) return t('WORKSPACE_V2.TASK_CREATE.NO_ASSIGNEE');
  const agent = agents.value.find(item => item.id === assignedToUserId.value);
  return agent?.name || currentUser.value.name || '';
});
const assigneeInitials = computed(() =>
  (assigneeName.value || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase()
);

const linkLabel = computed(() => {
  if (!link.value?.type) return t('WORKSPACE_V2.TASK_CREATE.LINK_NONE');
  if (link.value.label) return link.value.label;
  if (link.value.type === 'kanban_item')
    return t('WORKSPACE_V2.TASK_CREATE.LINK_CARD', { id: link.value.id });
  if (link.value.type === 'conversation')
    return t('WORKSPACE_V2.TASK_CREATE.LINK_CONVERSATION', {
      id: link.value.id,
    });
  return t('WORKSPACE_V2.TASK_CREATE.LINK_CONTACT', { id: link.value.id });
});

const runContactSearch = async query => {
  contactSearchToken += 1;
  const token = contactSearchToken;
  contactSearchError.value = false;
  if (query.trim().length < 2) {
    contactResults.value = [];
    contactSearching.value = false;
    return;
  }
  contactSearching.value = true;
  try {
    const response = await ContactAPI.search(query.trim(), 1);
    if (!alive || token !== contactSearchToken) return;
    const payload = response.data?.payload || response.data || [];
    contactResults.value = (
      Array.isArray(payload) ? payload : payload.data || []
    ).slice(0, 8);
  } catch {
    if (alive && token === contactSearchToken) contactSearchError.value = true;
  } finally {
    if (alive && token === contactSearchToken) contactSearching.value = false;
  }
};
watch(contactQuery, query => {
  if (contactSearchTimer) clearTimeout(contactSearchTimer);
  contactSearchTimer = setTimeout(() => runContactSearch(query), 300);
});

const pickContact = (contact, close) => {
  link.value = {
    type: 'contact',
    id: contact.id,
    label: contact.name || contact.email || String(contact.id),
  };
  contactQuery.value = '';
  contactResults.value = [];
  close?.();
};
const clearLink = close => {
  link.value = null;
  close?.();
};

const addSubtask = () => {
  subtasks.value.push({
    title: '',
    position: subtasks.value.length,
    created: false,
  });
};
const removeSubtask = index => subtasks.value.splice(index, 1);

const applyTemplate = () => {
  const template = availableTemplates.value.find(
    item => item.id === selectedTemplateId.value
  );
  if (!template) return;
  title.value = title.value || template.name || '';
  description.value = description.value || template.description || '';
  priority.value =
    ['none', 'low', 'medium', 'high', 'urgent'][template.default_priority] ||
    priority.value;
  labelIds.value = (template.default_label_ids || []).slice();
  subtasks.value = (template.default_subtasks || [])
    .map((subtask, index) => ({
      title: subtask.title || '',
      position: typeof subtask.position === 'number' ? subtask.position : index,
      created: false,
    }))
    .filter(subtask => subtask.title);
  if (description.value || labelIds.value.length) showMore.value = true;
};

const toggleLabel = id => {
  labelIds.value = labelIds.value.includes(id)
    ? labelIds.value.filter(item => item !== id)
    : [...labelIds.value, id];
};
const toggleReminder = value => {
  reminderOffsets.value = reminderOffsets.value.includes(value)
    ? reminderOffsets.value.filter(item => item !== value)
    : [...reminderOffsets.value, value];
};

const reminderOptions = computed(() => [
  { value: 5, label: t('WORKSPACE_V2.TASK_REMINDERS.MINUTES_5') },
  { value: 15, label: t('WORKSPACE_V2.TASK_REMINDERS.MINUTES_15') },
  { value: 60, label: t('WORKSPACE_V2.TASK_REMINDERS.MINUTES_60') },
  { value: 1440, label: t('WORKSPACE_V2.TASK_REMINDERS.MINUTES_1440') },
]);

const onAttachmentChange = event => {
  const file = event.target.files?.[0] || null;
  attachmentError.value = '';
  if (file && file.size > MAX_ATTACHMENT_BYTES) {
    attachmentFile.value = null;
    attachmentError.value = 'TOO_LARGE';
    return;
  }
  attachmentFile.value = file;
};

const isDirty = computed(
  () =>
    title.value.trim() !== (props.initialTitle || '').trim() ||
    description.value.trim() !== '' ||
    subtasks.value.some(subtask => subtask.title.trim()) ||
    !!attachmentFile.value ||
    labelIds.value.length > 0 ||
    reminderOffsets.value.length > 0 ||
    recurrenceEnabled.value ||
    dueDate.value !== (props.initialDueDate || null)
);

const requestClose = () => {
  if (saving.value) return;
  if (createdTask.value) {
    emit('created', { task: createdTask.value, keepEditing: false });
    emit('close');
    return;
  }
  if (isDirty.value && !confirmingDiscard.value) {
    confirmingDiscard.value = true;
    return;
  }
  emit('close');
};
const cancelDiscard = () => {
  confirmingDiscard.value = false;
  focusTitle();
};

const submit = async () => {
  if (saving.value) return;
  titleError.value = false;
  dueError.value = false;
  createError.value = '';
  if (!title.value.trim()) {
    titleError.value = true;
    focusTitle();
    return;
  }
  const due = buildDueAt(dueDate.value, dueTime.value);
  if (due.invalid) {
    dueError.value = true;
    return;
  }
  saving.value = true;
  attachmentUploading.value = !!attachmentFile.value;
  try {
    const result = await createTaskWithExtras(
      {
        task: createdTask.value,
        title: title.value,
        description: description.value,
        priority: priority.value,
        statusKey: statusKey.value,
        assignedToUserId: assignedToUserId.value,
        dueAt: due.value,
        reminderOffsets: reminderOffsets.value,
        recurrenceRule: recurrenceEnabled.value
          ? { frequency: recurrenceFrequency.value, interval: 1 }
          : null,
        labelIds: labelIds.value,
        link: link.value,
        subtasks: subtasks.value,
        attachmentFile: attachmentFile.value,
      },
      { statuses: catalog.value }
    );
    if (!alive) return;
    createdTask.value = result.task;
    result.createdSubtaskIndexes.forEach(index => {
      if (subtasks.value[index]) subtasks.value[index].created = true;
    });
    failedSubtaskTitles.value = result.failedSubtasks.map(item => item.title);
    attachmentError.value = result.attachmentError;
    if (!result.attachmentError) attachmentFile.value = null;
    if (failedSubtaskTitles.value.length || result.attachmentError) return;
    emit('created', { task: result.task, keepEditing: keepEditing.value });
    emit('close');
  } catch {
    if (alive) createError.value = 'CREATE_ERROR';
  } finally {
    if (alive) {
      saving.value = false;
      attachmentUploading.value = false;
    }
  }
};

const onFormKeydown = event => {
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
    event.preventDefault();
    submit();
  }
};

const submitLabel = computed(() =>
  createdTask.value
    ? t('WORKSPACE_V2.TASK_CREATE.RETRY_PENDING')
    : t('WORKSPACE_V2.TASK_CREATE.SUBMIT')
);
</script>

<template>
  <TaskEditorFrame
    :on-close="requestClose"
    :label="$t('WORKSPACE_V2.TASK_CREATE.TITLE')"
  >
    <div
      class="flex max-h-[94dvh] w-full flex-col"
      data-testid="task-create-dialog"
    >
      <header
        class="flex shrink-0 items-center justify-between gap-3 border-b border-n-weak px-5 py-4"
      >
        <h1 class="m-0 text-base font-semibold text-n-slate-12">
          {{ $t('WORKSPACE_V2.TASK_CREATE.TITLE') }}
        </h1>
        <button
          type="button"
          class="inline-flex size-9 items-center justify-center rounded-xl text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          :disabled="saving"
          :title="$t('WORKSPACE_V2.TASK_CREATE.CANCEL')"
          :aria-label="$t('WORKSPACE_V2.TASK_CREATE.CANCEL')"
          @click="requestClose"
        >
          <span class="i-ph-x size-4" aria-hidden="true" />
        </button>
      </header>

      <form
        class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5"
        @submit.prevent="submit"
        @keydown="onFormKeydown"
      >
        <p
          v-if="statusesLoading"
          class="m-0 text-sm text-n-slate-11"
          role="status"
        >
          {{ $t('WORKSPACE_V2.TASK_CREATE.LOADING_STATUSES') }}
        </p>
        <p v-else-if="statusesError" class="m-0 text-sm text-n-slate-11">
          {{ $t('WORKSPACE_V2.TASK_CREATE.STATUSES_FALLBACK') }}
        </p>

        <div v-if="availableTemplates.length" class="flex items-center gap-2">
          <label
            class="text-xs font-medium text-n-slate-11"
            for="task-create-template"
          >
            {{ $t('WORKSPACE_V2.TASK_CREATE.TEMPLATE') }}
          </label>
          <select
            id="task-create-template"
            v-model="selectedTemplateId"
            class="h-9 min-w-0 flex-1 rounded-xl border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
            :disabled="saving"
            @change="applyTemplate"
          >
            <option :value="null">
              {{ $t('WORKSPACE_V2.TASK_CREATE.NO_TEMPLATE') }}
            </option>
            <option
              v-for="template in availableTemplates"
              :key="template.id"
              :value="template.id"
            >
              {{ template.name }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1.5">
          <label
            class="text-xs font-medium text-n-slate-11"
            for="task-create-title"
          >
            {{ $t('WORKSPACE_V2.TASK_CREATE.FIELD_TITLE') }}
          </label>
          <input
            id="task-create-title"
            ref="titleInput"
            v-model="title"
            type="text"
            data-testid="task-create-title"
            class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-invalid="titleError"
            :placeholder="$t('WORKSPACE_V2.TASK_CREATE.FIELD_TITLE')"
            :disabled="saving"
            @keydown.enter.prevent="submit"
            @input="titleError = false"
          />
          <p v-if="titleError" class="m-0 text-xs text-n-ruby-11" role="alert">
            {{ $t('WORKSPACE_V2.TASK_CREATE.TITLE_REQUIRED') }}
          </p>
        </div>

        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ $t('WORKSPACE_V2.TASK_CREATE.FIELD_DUE') }}
          </span>
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-for="key in ['today', 'tomorrow', 'workday']"
              :key="key"
              type="button"
              class="inline-flex h-9 items-center rounded-full px-3 text-xs font-bold"
              :class="
                activeChip === key
                  ? 'bg-n-brand text-white'
                  : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
              "
              :aria-pressed="activeChip === key"
              :disabled="saving"
              @click="pickChip(key)"
            >
              {{ chipLabel(key) }}
            </button>
            <TaskAnchoredPopover
              :label="$t('WORKSPACE_V2.TASK_CREATE.OTHER_DATE')"
              :min-width="280"
            >
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  data-testid="task-create-date"
                  class="inline-flex h-9 items-center gap-1.5 rounded-full bg-n-alpha-2 px-3 text-xs font-bold text-n-slate-12 hover:bg-n-alpha-3"
                  :disabled="saving"
                  @click="toggle"
                >
                  <span
                    class="i-ph-calendar-blank size-3.5"
                    aria-hidden="true"
                  />
                  {{ $t('WORKSPACE_V2.TASK_CREATE.OTHER_DATE') }}
                </button>
              </template>
              <template #default>
                <DatePicker v-model="dueDate" compact accent="teal" />
              </template>
            </TaskAnchoredPopover>
            <span class="text-xs text-n-slate-11">{{ dueLabel }}</span>
            <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
              {{ $t('WORKSPACE_V2.TASK_CREATE.FIELD_TIME') }}
              <input
                v-model="dueTime"
                type="time"
                class="h-9 rounded-xl border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
                :disabled="saving || !dueDate"
                @input="dueError = false"
              />
            </label>
          </div>
          <p v-if="dueError" class="m-0 text-xs text-n-ruby-11" role="alert">
            {{ $t('WORKSPACE_V2.TASK_CREATE.DUE_INVALID') }}
          </p>
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('WORKSPACE_V2.TASK_CREATE.FIELD_ASSIGNEE') }}
            </span>
            <TaskAnchoredPopover
              :label="$t('WORKSPACE_V2.TASK_CREATE.FIELD_ASSIGNEE')"
              :min-width="260"
            >
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  data-testid="task-create-assignee"
                  class="inline-flex h-11 w-full items-center gap-2 rounded-xl border border-n-weak px-3 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  :disabled="saving"
                  :title="assigneeName"
                  @click="toggle"
                >
                  <span
                    class="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-n-alpha-3 text-[10px] font-bold"
                    aria-hidden="true"
                  >
                    {{ assigneeInitials }}
                  </span>
                  <span class="min-w-0 truncate">{{ assigneeName }}</span>
                </button>
              </template>
              <template #default="{ close }">
                <ul class="m-0 max-h-64 list-none overflow-y-auto p-0">
                  <li>
                    <button
                      type="button"
                      class="w-full rounded-lg px-2 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2"
                      @click="
                        assignedToUserId = null;
                        close();
                      "
                    >
                      {{ $t('WORKSPACE_V2.TASK_CREATE.NO_ASSIGNEE') }}
                    </button>
                  </li>
                  <li v-for="agent in agents" :key="agent.id">
                    <button
                      type="button"
                      class="w-full rounded-lg px-2 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                      @click="
                        assignedToUserId = agent.id;
                        close();
                      "
                    >
                      {{ agent.name }}
                    </button>
                  </li>
                </ul>
              </template>
            </TaskAnchoredPopover>
          </div>

          <div class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('WORKSPACE_V2.TASK_CREATE.FIELD_LINK') }}
            </span>
            <TaskAnchoredPopover
              :label="$t('WORKSPACE_V2.TASK_CREATE.FIELD_LINK')"
              :min-width="300"
            >
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  data-testid="task-create-link"
                  class="inline-flex h-11 w-full items-center gap-2 rounded-xl border border-n-weak px-3 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  :disabled="saving"
                  :title="linkLabel"
                  @click="toggle"
                >
                  <span class="i-ph-link size-4 shrink-0" aria-hidden="true" />
                  <span class="min-w-0 truncate">{{ linkLabel }}</span>
                  <span
                    class="ml-auto shrink-0 text-xs font-bold text-n-brand"
                    aria-hidden="true"
                  >
                    {{ $t('WORKSPACE_V2.TASK_CREATE.LINK_CHANGE') }}
                  </span>
                </button>
              </template>
              <template #default="{ close }">
                <div class="flex w-[19rem] max-w-full flex-col gap-2">
                  <button
                    type="button"
                    class="w-full rounded-lg px-2 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2"
                    @click="clearLink(close)"
                  >
                    {{ $t('WORKSPACE_V2.TASK_CREATE.LINK_NONE') }}
                  </button>
                  <input
                    v-model="contactQuery"
                    type="search"
                    class="h-9 w-full rounded-xl border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
                    :placeholder="
                      $t('WORKSPACE_V2.TASK_CREATE.LINK_SEARCH_CONTACT')
                    "
                    :aria-label="
                      $t('WORKSPACE_V2.TASK_CREATE.LINK_SEARCH_CONTACT')
                    "
                  />
                  <p
                    v-if="contactSearching"
                    class="m-0 text-xs text-n-slate-11"
                  >
                    {{ $t('WORKSPACE_V2.TASK_CREATE.SEARCHING') }}
                  </p>
                  <p
                    v-else-if="contactSearchError"
                    class="m-0 text-xs text-n-ruby-11"
                    role="alert"
                  >
                    {{ $t('WORKSPACE_V2.TASK_CREATE.SEARCH_ERROR') }}
                  </p>
                  <ul class="m-0 max-h-48 list-none overflow-y-auto p-0">
                    <li v-for="contact in contactResults" :key="contact.id">
                      <button
                        type="button"
                        class="w-full rounded-lg px-2 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                        @click="pickContact(contact, close)"
                      >
                        {{ contact.name || contact.email || contact.id }}
                      </button>
                    </li>
                  </ul>
                </div>
              </template>
            </TaskAnchoredPopover>
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <span
            id="task-create-subtasks-label"
            class="text-xs font-medium text-n-slate-11"
          >
            {{ $t('WORKSPACE_V2.TASK_CREATE.SUBTASKS') }}
          </span>
          <div
            v-for="(subtask, index) in subtasks"
            :key="index"
            class="flex items-center gap-2"
          >
            <span
              class="i-ph-check-square size-4 shrink-0 text-n-slate-10"
              aria-hidden="true"
            />
            <input
              v-model="subtask.title"
              type="text"
              class="h-10 min-w-0 flex-1 rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
              :disabled="saving || subtask.created"
              :aria-label="
                $t('WORKSPACE_V2.TASK_CREATE.SUBTASK_LABEL', { n: index + 1 })
              "
              :placeholder="$t('WORKSPACE_V2.TASK_CREATE.SUBTASK_PLACEHOLDER')"
            />
            <span
              v-if="subtask.created"
              class="i-ph-check size-4 shrink-0 text-n-teal-11"
              :title="$t('WORKSPACE_V2.TASK_CREATE.SUBTASK_CREATED')"
              :aria-label="$t('WORKSPACE_V2.TASK_CREATE.SUBTASK_CREATED')"
            />
            <button
              v-else
              type="button"
              class="inline-flex size-9 shrink-0 items-center justify-center rounded-xl text-n-slate-10 hover:bg-n-alpha-2"
              :disabled="saving"
              :title="$t('WORKSPACE_V2.TASK_CREATE.SUBTASK_REMOVE')"
              :aria-label="$t('WORKSPACE_V2.TASK_CREATE.SUBTASK_REMOVE')"
              @click="removeSubtask(index)"
            >
              <span class="i-ph-x size-3.5" aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            data-testid="task-create-add-subtask"
            class="inline-flex h-9 w-fit items-center gap-1.5 rounded-xl px-2 text-xs font-bold text-n-brand hover:bg-n-alpha-2"
            :disabled="saving"
            @click="addSubtask"
          >
            <span class="i-ph-plus size-3.5" aria-hidden="true" />
            {{ $t('WORKSPACE_V2.TASK_CREATE.ADD_SUBTASK') }}
          </button>
        </div>

        <button
          type="button"
          data-testid="task-create-more"
          class="inline-flex h-10 w-fit items-center gap-1.5 rounded-xl px-2 text-xs font-bold text-n-slate-11 hover:bg-n-alpha-2"
          :aria-expanded="showMore"
          @click="showMore = !showMore"
        >
          <span
            class="size-3.5"
            :class="showMore ? 'i-ph-minus' : 'i-ph-plus'"
            aria-hidden="true"
          />
          {{ $t('WORKSPACE_V2.TASK_CREATE.MORE') }}
        </button>

        <div v-if="showMore" class="flex flex-col gap-4">
          <div class="flex flex-col gap-1.5">
            <label
              class="text-xs font-medium text-n-slate-11"
              for="task-create-description"
            >
              {{ $t('WORKSPACE_V2.TASK_FIELDS.DESCRIPTION') }}
            </label>
            <textarea
              id="task-create-description"
              v-model="description"
              rows="3"
              class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              :disabled="saving"
            />
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <label
                class="text-xs font-medium text-n-slate-11"
                for="task-create-priority"
              >
                {{ $t('WORKSPACE_V2.TASK_FIELDS.PRIORITY') }}
              </label>
              <select
                id="task-create-priority"
                v-model="priority"
                class="h-11 rounded-xl border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
                :disabled="saving"
              >
                <option value="none">
                  {{ $t('WORKSPACE_V2.TASK_CREATE.PRIORITY_NONE') }}
                </option>
                <option value="low">
                  {{ $t('WORKSPACE_V2.TASK_CREATE.PRIORITY_LOW') }}
                </option>
                <option value="medium">
                  {{ $t('WORKSPACE_V2.TASK_CREATE.PRIORITY_MEDIUM') }}
                </option>
                <option value="high">
                  {{ $t('WORKSPACE_V2.TASK_CREATE.PRIORITY_HIGH') }}
                </option>
                <option value="urgent">
                  {{ $t('WORKSPACE_V2.TASK_CREATE.PRIORITY_URGENT') }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label
                class="text-xs font-medium text-n-slate-11"
                for="task-create-attachment"
              >
                {{ $t('WORKSPACE_V2.TASK_ATTACHMENTS.ADD') }}
              </label>
              <input
                id="task-create-attachment"
                type="file"
                data-testid="task-create-attachment"
                class="text-sm text-n-slate-11"
                :disabled="saving"
                @change="onAttachmentChange"
              />
              <p class="m-0 text-xs text-n-slate-10">
                {{ $t('WORKSPACE_V2.TASK_ATTACHMENTS.HINT') }}
              </p>
            </div>
          </div>
          <div v-if="availableLabels.length" class="flex flex-col gap-1.5">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('WORKSPACE_V2.TASK_HEADER.LABELS') }}
            </span>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="label in availableLabels"
                :key="label.id"
                type="button"
                class="rounded-lg border border-n-weak px-2.5 py-1 text-xs"
                :class="
                  labelIds.includes(label.id)
                    ? 'border-n-brand font-bold text-n-slate-12'
                    : 'text-n-slate-11'
                "
                :aria-pressed="labelIds.includes(label.id)"
                :disabled="saving"
                @click="toggleLabel(label.id)"
              >
                {{ label.name }}
              </button>
            </div>
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('WORKSPACE_V2.TASK_FIELDS.REMINDERS') }}
              </span>
              <div class="flex flex-wrap gap-3">
                <label
                  v-for="option in reminderOptions"
                  :key="option.value"
                  class="inline-flex items-center gap-1.5 text-sm text-n-slate-11"
                >
                  <input
                    type="checkbox"
                    :checked="reminderOffsets.includes(option.value)"
                    :disabled="saving"
                    @change="toggleReminder(option.value)"
                  />
                  {{ option.label }}
                </label>
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('WORKSPACE_V2.TASK_FIELDS.REPEAT') }}
              </span>
              <div class="flex items-center gap-2">
                <label
                  class="inline-flex items-center gap-1.5 text-sm text-n-slate-11"
                >
                  <input
                    v-model="recurrenceEnabled"
                    type="checkbox"
                    :disabled="saving"
                  />
                  {{ $t('WORKSPACE_V2.TASK_CREATE.REPEAT_YES') }}
                </label>
                <select
                  v-if="recurrenceEnabled"
                  v-model="recurrenceFrequency"
                  class="h-9 rounded-xl border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
                  :disabled="saving"
                >
                  <option value="daily">
                    {{ $t('WORKSPACE_V2.TASK_RECURRENCE.DAILY') }}
                  </option>
                  <option value="weekly">
                    {{ $t('WORKSPACE_V2.TASK_RECURRENCE.WEEKLY') }}
                  </option>
                  <option value="monthly">
                    {{ $t('WORKSPACE_V2.TASK_RECURRENCE.MONTHLY') }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </form>

      <footer
        class="flex shrink-0 flex-col gap-3 border-t border-n-weak px-5 py-4"
      >
        <p
          v-if="attachmentUploading"
          class="m-0 text-sm text-n-slate-11"
          role="status"
        >
          {{ $t('WORKSPACE_V2.TASK_ATTACHMENTS.UPLOADING') }}
        </p>
        <p
          v-if="attachmentError"
          class="m-0 text-sm text-n-ruby-11"
          role="alert"
          data-testid="task-create-attachment-error"
        >
          {{ $t(`WORKSPACE_V2.TASK_ATTACHMENTS.${attachmentError}`) }}
        </p>
        <p
          v-if="failedSubtaskTitles.length"
          class="m-0 text-sm text-n-ruby-11"
          role="alert"
          data-testid="task-create-partial"
        >
          {{
            $t('WORKSPACE_V2.TASK_CREATE.PARTIAL_SUBTASKS', {
              subtasks: failedSubtaskTitles.join(', '),
            })
          }}
        </p>
        <p
          v-if="createError"
          class="m-0 text-sm text-n-ruby-11"
          role="alert"
          data-testid="task-create-error"
        >
          {{ $t('WORKSPACE_V2.TASK_CREATE.CREATE_ERROR') }}
        </p>
        <div
          v-if="confirmingDiscard"
          class="flex flex-wrap items-center justify-between gap-2"
          role="alertdialog"
          :aria-label="$t('WORKSPACE_V2.TASK_CREATE.DISCARD_TITLE')"
        >
          <span class="text-sm text-n-slate-11">
            {{ $t('WORKSPACE_V2.TASK_CREATE.DISCARD_TITLE') }}
          </span>
          <span class="flex gap-2">
            <button
              type="button"
              class="inline-flex h-10 items-center rounded-xl border border-n-weak px-3 text-sm text-n-slate-12"
              @click="cancelDiscard"
            >
              {{ $t('WORKSPACE_V2.TASK_CREATE.DISCARD_KEEP') }}
            </button>
            <button
              type="button"
              data-testid="task-create-discard"
              class="inline-flex h-10 items-center rounded-xl bg-n-ruby-9 px-3 text-sm font-bold text-white"
              @click="emit('close')"
            >
              {{ $t('WORKSPACE_V2.TASK_CREATE.DISCARD_CONFIRM') }}
            </button>
          </span>
        </div>
        <div v-else class="flex flex-wrap items-center justify-end gap-3">
          <label
            class="mr-auto inline-flex items-center gap-1.5 text-sm text-n-slate-11"
          >
            <input v-model="keepEditing" type="checkbox" :disabled="saving" />
            {{ $t('WORKSPACE_V2.TASK_CREATE.KEEP_EDITING') }}
          </label>
          <button
            type="button"
            class="inline-flex h-11 items-center rounded-xl border border-n-weak px-4 text-sm text-n-slate-12 hover:bg-n-alpha-2"
            :disabled="saving"
            @click="requestClose"
          >
            {{ $t('WORKSPACE_V2.TASK_CREATE.CANCEL') }}
          </button>
          <button
            type="button"
            data-testid="task-create-submit"
            class="inline-flex h-11 items-center gap-2 rounded-xl bg-n-brand px-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="saving || !title.trim()"
            :title="$t('WORKSPACE_V2.TASK_CREATE.SUBMIT_HINT')"
            @click="submit"
          >
            <span
              v-if="saving"
              class="i-ph-circle-notch size-4 animate-spin"
              aria-hidden="true"
            />
            {{ submitLabel }}
          </button>
        </div>
      </footer>
    </div>
  </TaskEditorFrame>
</template>
